import React, { useCallback, useMemo, useState } from 'react';
import startCase from 'lodash/startCase';
import { useSnackbar } from 'notistack';
import { Add as AddIcon } from '@mui/icons-material';
import { getTasks } from '../../../apiClients/backendApi';
import useAsync from '../../../hooks/useAsync';
import DataGridDyl from '../../../components/DataGridDyl';
import { useTitle } from '../../../systems/TitleManager';
import { useLevels } from '@dyl/systems/LevelsManager';
import NewTaskModal from './NewTaskModal';
import ScreenActions from '@dyl/components/ScreenActions';
import LinkButton from '@dyl/components/LinkButton';
import { Fab, Grid, MenuItem, TextField, Box } from '@mui/material';
import quickDateFormat from '@dyl/utils/quickDateFormat';
import deleteTask from "@dyl/apiClients/backendApi/deleteTask";
import GenericDeleteAction from "@dyl/components/GenericDeleteAction";
import withLevel from "@dyl/utils/withLevel";
import TaskTypes from "@dyl/task-types";
import useChangeEvent from "@dyl/hooks/useChangeEvent";
import useStyles from './styles';
import useLogic from './logic';
import { CollapsibleButton } from '@dyl/components/CollapsibleButtonGroup';
import { CollapsibleAssetButtonGroup } from '@dyl/components/CollapsibleButtonGroup';


export default withLevel(TaskList);

function TaskList() {
    useTitle('Tasks');
    const { enqueueSnackbar } = useSnackbar();
    const { globalLevel } = useLevels();
    const classes = useStyles();

    const [taskTypeFilter, setTaskTypeFilter] = useState('all');
    const onTaskTypeFilterChange = useChangeEvent(setTaskTypeFilter);
    const [loading, setLoading] = useState(true);
    const [triggerReload, setTriggerReload] = useState();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [selectedArray, setSelectedArray] = useState([]);
    const { selectedIds, setSelectedIds,
        selectAll,
        isExporting, onExportClick } = useLogic();

    const tasks = useAsync(async () => {
        setLoading(true);
        try {
            return await getTasks(globalLevel.level_id);
        }
        catch {
            enqueueSnackbar('Failed to load tasks', { variant: 'error' });
        }
        finally {
            setLoading(false);
        }
    }, [triggerReload, globalLevel]) ?? [];

    const currentTaskTypes = useMemo(() => {
        const taskTypes = TaskTypes[globalLevel.label];
        return taskTypes;
    }, [globalLevel.label]);

    const columns = useMemo(
        /** @returns {import('@mui/x-data-grid').GridColDef[]} */
        () => {
            const cols = [
                { headerName: 'Task ID', field: 'task_id_name', flex: 3 },
                {
                    headerName: 'Component ID',
                    field: 'test_names',
                    flex: 3,
                    renderCell({ value }) {
                      const joined = Array.isArray(value) ? value.join(', ') : String(value);
                      return (
                        <div
                          title={joined}
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: '100%',
                          }}
                        >
                          {joined}
                        </div>
                      );
                    }
                },
                { headerName: 'Skill', field: 'skill_id', flex: 2, valueFormatter({ value }) {
                    return startCase(value);
                }
                },
                {
                    headerName: 'Task Type', field: 'task_type_id', flex: 3,
                    valueFormatter({ value }) {
                        return currentTaskTypes.find(t => t.id === value)?.label ?? 'Unknown';
                    }
                },
                // TODO: remove condition once backend implementation complete
                { headerName: 'Last modified', field: 'updated_at', flex: 3, valueFormatter({ value }) {
                    return value ? quickDateFormat(value) : 'Unavailable';
                }
                },
            ];

            if (globalLevel.label === 'A2') {
                cols.push({
                    headerName: 'ARC status',
                    field: 'is_arc_published',
                    flex: 2,
                    sortable: false,
                    renderCell({ value }) {
                        if (value === null || value === undefined) return '';
                        if (typeof value === 'boolean') return value ? 'Sent' : 'Draft';
                        return String(value);
                    }
                });
            }

            cols.push(
                { headerName: 'Anchor', field: 'anchor', flex: 1, renderCell({ row, value }) {
                    return row.skill_id.toLowerCase() === 'speaking' ? 'N/A' : (value ? 'True' : 'False');
                }
                },
                { headerName: 'Delete', field: 'ADMIN_DELETE', flex: 2, renderCell({ row }) {
                    return (<GenericDeleteAction
                            data={row}
                            disabled={row.anchor}
                            promptTexts={
                                {
                                    dialogTitle: () => 'Delete Task?',
                                    dialogText: (data) => `Are you sure you want to delete task "${data.task_id_name}"?`,
                                    ongoingNotification: (data) => `Deleting task "${data.task_id_name}"...`,
                                    successNotification: (data) => `Deleted "${data.task_id_name}".`,
                                    failureNotification: (data, response) => `The task you are trying to delete is associated with the following test components(s): `
                                        + `${response.tests.join(', ')}.\r\n`
                                        + `Please remove the task from the associated test components(s) in order to delete it.`,

                                    errorNotification: (data) => `Failed to delete "${data.task_id_name}".`
                                }
                            }
                            deleteAction={(data) => deleteTask(globalLevel.level_id, data.task_id)}
                            responseSuccessPredicate={(response) => response?.deleted || response?.success}
                            onDelete={() => setTriggerReload(true)}
                        />)
                }
                }
            );

            return cols;
        }, [globalLevel, currentTaskTypes]
    );

    const getRowUrl = useCallback(({ row: task }) => {
        return `/${globalLevel.label}/tasks/${task.task_type_id}/${task.task_id}`;
    }, [globalLevel]);

    const [isNewMenuOpen, setNewMenuOpen] = useState(false);

    const filteredTasks = useMemo(() => {
        if (taskTypeFilter === 'all')
            return tasks;
        return tasks.filter(t => t.task_type_id === taskTypeFilter)
    }, [tasks, taskTypeFilter]);

    const handleSelectedIds = (page, ids) => {
        setSelectedArray(prev => {
            const updated = { ...prev, [page]: ids };
            const allSelected = [...new Set(Object.values(updated).flat())];
            setSelectedIds(allSelected)

            return updated;
        });
    };

    return (
        <>
            <DataGridDyl
                rows={filteredTasks}
                columns={columns}
                getRowId={getRowId}
                getRowUrl={getRowUrl}
                searchField='task_id_name'
                variant='links'
                loading={loading}
                selectionType='checkboxes'
                selectionModel={selectedIds}
                onSelectionModelChange={setSelectedIds}
                handleSelectedIds = {handleSelectedIds}
                selectedArray = {selectedArray}
                overrideSelectAll={selectAll}
                fromTaskTab={true}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                paginationModel={{ page, pageSize }}
                toolbarContent={(
                    <>
                    <Box className={classes.actionLine}>
                        <CollapsibleAssetButtonGroup className={classes.actionGroup} color='primary'>
                            <CollapsibleButton down='md' onClick={()=> onExportClick(selectedIds)} disabled={isExporting}>
                                Export Task(s)
                            </CollapsibleButton>
                        </CollapsibleAssetButtonGroup>
                    </Box>
                </>
                )}
            >
                <Grid item xs={2} spacing={0}>
                    <TextField
                        style={{ marginLeft: '8px', width: '280px' }}
                        variant='filled'
                        InputProps={{ disableUnderline: true }}
                        select
                        label="Task Type"
                        value={taskTypeFilter}
                        onChange={onTaskTypeFilterChange}
                    >
                        <MenuItem key={'all'} value={'all'} selected>All</MenuItem>
                        {currentTaskTypes.map(({ label, id }) => (
                            <MenuItem key={id} value={id}>
                                {label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </DataGridDyl>

            <ScreenActions>
                <LinkButton
                    component={Fab}
                    color='primary'
                    variant='extended'
                    size='large'
                    onClick={() => setNewMenuOpen(true)}
                >
                    <AddIcon />&nbsp;New
                </LinkButton>
            </ScreenActions>

            <NewTaskModal open={isNewMenuOpen} onClose={() => setNewMenuOpen(false)} />
        </>
    );
}

const getRowId = task => task.task_id;